import Microgame from '../engine/Microgame.js';

export default class RopeGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Corde à sauter !`, en: `Jump Rope!`, es: `¡Cuerda!` },
            controls: { fr: `Appuie sur l'écran pour sauter en rythme`, en: `Tap the screen to jump in rhythm`, es: `Toca la pantalla para saltar al ritmo` },
            objective: { fr: `Esquive la corde qui tourne`, en: `Dodge the spinning rope`, es: `Esquiva la cuerda que gira` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--accent-color)" stroke-width="2"><path d="M5 14h14"/><path d="M12 2A10 10 0 0 0 2 12"/><path d="M12 22a10 10 0 0 0 10-10"/></svg>`,
            actions: [
                { type: 'wait', duration: 460 },
                { type: 'tap', x: 50, y: 78, duration: 180 },
                { type: 'wait', duration: 900 },
                { type: 'tap', x: 50, y: 78, duration: 180 },
                { type: 'wait', duration: 900 },
                { type: 'tap', x: 50, y: 78, duration: 180 },
                { type: 'wait', duration: 700 },
            ],
            loopDuration: 3800
        };
    }
    constructor(engine) {
        super(engine);
        const speedMult = engine.speedMultiplier || 1.0;
        const adj = 1 + (speedMult - 1) * 0.4;
        this.duration = 4000 * (speedMult / adj);

        this.character = null;
        this.rope = null;
        this.isJumping = false;
        this.jumpHeight = 0;
        this.ropeAngle = -Math.PI / 2; // Start at top
        this.ropeSpeed = (3.8 + (speedMult - 1) * 2.0);
        this.ropeY = 0; 
        this.jumpsScored = 0; 
        this.lastSwing = -1; 

        // Ground
        const ground = document.createElement('div');
        ground.style.position = 'absolute';
        ground.style.width = '100%';
        ground.style.height = '20%';
        ground.style.bottom = '0';
        ground.style.background = 'transparent';
        ground.style.borderTop = `2px solid ${this.primaryColor}`;
        ground.style.boxShadow = `0 -5px 20px ${this.primaryColor}`;
        this.container.appendChild(ground);

        // Character
        this.character = document.createElement('div');
        this.character.style.position = 'absolute';
        this.character.style.width = '40px';
        this.character.style.height = '60px';
        this.character.style.background = this.primaryColor;
        this.character.style.bottom = '20%';
        this.character.style.left = '50%';
        this.character.style.transform = 'translateX(-50%)';
        this.character.style.borderRadius = '5px';
        this.character.style.boxShadow = `0 0 15px ${this.primaryColor}`;
        this.character.style.zIndex = '10'; // Middle layer
        this.container.appendChild(this.character);

        // Rope (Visualized as a curve or line)
        // Let's use a div that scales height to simulate swinging
        this.rope = document.createElement('div');
        this.rope.style.position = 'absolute';
        this.rope.style.width = '80%';
        this.rope.style.height = '2px';
        this.rope.style.background = this.secondaryColor;
        this.rope.style.bottom = '20%'; 
        this.rope.style.left = '25%'; // Centered
        this.rope.style.width = '50%'; // Further reduced horizontal span
        this.rope.style.borderRadius = '1px';
        this.rope.style.boxShadow = `0 0 4px ${this.secondaryColor}`;
        this.rope.style.willChange = 'transform, z-index';
        this.rope.style.transition = 'none'; 
        this.rope.style.zIndex = '5'; // Initial back
        this.container.appendChild(this.rope);

        this.lastY = 0;
        this.lastZIndex = '10'; // Cache state

        // Input
        this.onTap = this.onTap.bind(this);
        this.engine.input.element.addEventListener('pointerdown', this.onTap);
    }

    onTap() {
        if (this.isJumping || !this.active) return;
        this.isJumping = true;

        // Dynamic Jump Duration based on Rope Speed
        // Base duration: 1000ms.
        // As speed increases, duration decreases to ensure we land in time for next jump.
        // One full rotation = 2PI / ropeSpeed.
        // We want jump to be slightly faster than a full rotation? Or roughly 70% of rotation?

        let jumpDuration = 1000;
        if (this.ropeSpeed > 0) {
            // Target: Jump should be fast enough to land before rope returns.
            // Safe safe: 80% of rotation time.
            const rotationTime = (Math.PI * 2) / this.ropeSpeed;
            jumpDuration = Math.min(1000, rotationTime * 800);
            // rotationTime is in seconds. * 1000 to ms. * 0.8 safety.
        }

        // Clamp
        jumpDuration = Math.max(300, jumpDuration);

        // Jump Animation
        this.character.animate([
            { transform: 'translateX(-50%) translateY(0)' },
            { transform: 'translateX(-50%) translateY(-120px)', offset: 0.5 },
            { transform: 'translateX(-50%) translateY(0)' }
        ], { duration: jumpDuration, easing: 'ease-in-out' }).onfinish = () => {
            this.isJumping = false;
        };
    }

    update(dt) {
        // Swing Rope
        this.ropeAngle += this.ropeSpeed * (dt / 1000);

        // Infinite Mode Logic
        if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite_no_timer') {
            // Slowly accelerate per second
            this.ropeSpeed += 0.05 * (dt / 1000);
        }

        // Simulate 3D swing: sin(angle) controls vertical position, cos(angle) controls depth
        const vertical = Math.sin(this.ropeAngle); // 1 = bottom, -1 = top
        const depth = Math.cos(this.ropeAngle); // 1 = front, -1 = back

        // Collision Check: If rope is at bottom (vertical > 0.96) AND character is NOT jumping, HIT.
        // Tightened from 0.9 to 0.96 for more precise jumping requirement.
        if (vertical > 0.96) {
            if (!this.isJumping) {
                this.lose();
                this.character.style.background = '#ff0000';
                this.rope.style.background = '#ff0000';
                return;
            }
        }

        // Infinite Score Logic: Count rotations
        if (this.engine.activeChallenge && this.engine.activeChallenge.mode === 'infinite_no_timer') {
            const rotations = Math.floor((this.ropeAngle - Math.PI / 2) / (Math.PI * 2)); // Shift phase
            if (rotations > this.jumpsScored) {
                this.jumpsScored = rotations;
                this.engine.challengesCompleted++;
                this.engine.score = this.engine.challengesCompleted;
                this.engine.updateHUD();

                // Bump speed slightly on score
                this.ropeSpeed += 0.2;
            }
        }

        // Visual Update with Transform (Motion Blur Simulation)
        const rangePx = 130; 
        const yOffset = (1 - vertical) * -rangePx; // Move UP.
        const dy = Math.abs(yOffset - (this.lastY || 0));
        this.lastY = yOffset;

        // Width scale for depth (smaller when behind, wider when front)
        const scaleX = 0.8 + (depth + 1) * 0.2; // 0.8 to 1.2

        // Vertical stretch to fight stroboscopic effect
        // Now using actual frame-to-frame delta (dy) to fill gaps
        const vSpeedFactor = Math.abs(depth);
        const stretchY = 1 + Math.max(vSpeedFactor * this.ropeSpeed * 0.35, dy / 4);
        const opacity = 1 - (vSpeedFactor * 0.4); // Slightly transparent when moving fast

        this.rope.style.transform = `translate3d(0, ${yOffset}px, 0) scale(${scaleX}, ${stretchY})`;
        this.rope.style.opacity = opacity;

        // Z-index: if depth < 0 (back), behind char.
        const newZ = depth < 0 ? '5' : '15'; 
        if (newZ !== this.lastZIndex) {
            this.rope.style.zIndex = newZ;
            this.lastZIndex = newZ;
        }

        // Win if survived duration (default behavior handled by timeout if not infinite)
    }

    onTimeout() {
        return true; // Win if time runs out (survival)
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointerdown', this.onTap);
    }
}
